/**
 * Highlights page.
 *
 * @module app/pages/HighlightsPage
 */
import BasePage from './BasePage';
import BodyStateAdapter from '../components/BodyStateAdapter';
import DeviceDetector from '../components/DeviceDetector';

/**
 * Internal state flag used to
 * determine whether page has been
 * rendered or not
 *
 * @type {Boolean}
 */
var rendered = false;

/**
 * Page content in the form of a jQuery element
 *
 * @type {Object}
 */
var content = null;

/**
 * Page dependencies
 *
 * @type {Object}
 */
var deps = null;

/**
 * Page arguments
 *
 * @type {Object}
 */
var args = null;

/**
 * Function used to bind event handles to
 * various buttons/links on the page
 *
 * @param   {Object} content jQuery object containing
 *                           the page content
 * @param   {Object} deps    Page dependencies
 *
 * @returns {void}
 *
 * @private
 */
var bindNavigation = function (pageContent, pageDeps, pageArgs) {

    content = pageContent;
    deps = pageDeps;
    args = pageArgs;
};

/**
 * Contains functions exposed by this component
 * as an API
 *
 * @type {Object}
 */
var api = {

    /**
     * Return the URL associated with this page
     * based on the args
     *
     * @param   {Object} args Page arguments
     *
     * @returns {String}      Compiled URL based on args
     *
     * @public
     */
    getContentURL: function (args) {

        if(args.slugUrl) {
            return '/betting/' + args.locale + '/apps/promotions/offer/' + args.slugUrl;
        } 
        return '/betting/' + args.locale + '/apps/promotions';
    },

    /**
     * Function called after the DOM ready event
     * is fired
     *
     * @param   {Object} deps Object that contains page
     *                        dependencies
     *
     * @returns {void}
     *
     * @public
     */
    postReady: function (deps, content, args) {

        BodyStateAdapter.init(BasePage.jQuery, 'promotions', args.sport);

        if (deps.ready) {
            deps.ready('promotions');
        }
    },

    /**
     * Function called after rendering the page
     *
     * @param   {Object} deps       Object that contains page
     *                              dependencies
     * @param   {Object} content    jQuery object that contains
     *                              this page
     *
     * @returns {void}
     *
     * @public
     */
    postRender: function () {
        if (window.WH && window.WH.bootstrapper){
            const promotionsFlowFeature =  window.WH.bootstrapper.features['promotions-flow'];
            const isDesktop = DeviceDetector.isDesktop();
            const isTablet = DeviceDetector.isTablet();

            const viewMode = (isDesktop || isTablet) ? 'desktop' : 'mobile';

            if(promotionsFlowFeature){

                promotionsFlowFeature.onStartError(({ error: message }) => {
                    window.console.error('Error in starting promotions: ', message);
                });

                promotionsFlowFeature.onStopError(({ error: message }) => {
                    window.console.error('Error in stopping promotions: ', message);
                });

                promotionsFlowFeature.getState().then(({ state }) => {
                    if(state !== 'STOPPED'){
                        promotionsFlowFeature.stop();
                    } 

                    promotionsFlowFeature.start({
                        containerSelector: '#promotions-micro-app-container',
                        viewMode: viewMode,
                    });
                
                });     
            }

            BasePage.switchTab('Promotions');
        }
    },

    bindNavigation: bindNavigation,

    /**
     * Changes rendered state flag to true
     *
     * @public
     */
    setRendered: function () {

        rendered = true;
    },

    /**
     * Changes rendered state flag to false
     *
     * @public
     */
    setUnrendered: function () {  
        if (window.WH && window.WH.bootstrapper){
            const promotionsFlowFeature =  window.WH.bootstrapper.features['promotions-flow'];
            
            if(promotionsFlowFeature){
                promotionsFlowFeature.getState().then(({ state }) => {
                    if(state !== 'STOPPED'){
                        promotionsFlowFeature.stop();
                    }
                });
            }
        }

        content = null;
        deps = null;
        args = null;
        rendered = false;
    },

    /**
     * Returns the value of the internal
     * rendered state flag
     *
     * @returns {Boolean} Returns true or false
     *
     * @public
     */
    isRendered: function () {

        return rendered;
    },

    /**
     * Slug to identify the current page
     */
    id: 'promotions',

};

export default api;
